class ShippingNotice extends HTMLElement {
  constructor() {
    super();
    this.messageEl = this.querySelector('[data-rewards-message]');
    this.hintEl = this.querySelector('[data-rewards-hint]');
    this.barEl = this.querySelector('[data-rewards-progress-bar]');
    this.fillEl = this.querySelector('[data-rewards-progress-fill]');
    this.badgesEl = this.querySelector('[data-rewards-badges]');
    this.shippingMark = this.querySelector('[data-rewards-mark="shipping"]');
    this.giftMark = this.querySelector('[data-rewards-mark="gift"]');
  }

  connectedCallback() {
    this.updateState();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'data-cart-total-amount' ||
            mutation.attributeName === 'data-has-subscription' ||
            mutation.attributeName === 'data-gift-threshold' ||
            mutation.attributeName === 'data-otp-shipping-discount' ||
            mutation.attributeName === 'data-otp-shipping-threshold' ||
            mutation.attributeName === 'data-sub-shipping-discount' ||
            mutation.attributeName === 'data-sub-shipping-threshold')
        ) {
          this.updateState();
          return;
        }
      }
    });
    observer.observe(this, { attributes: true });

    document.addEventListener('cart:updated', () => {
      this.refreshFromCart();
    });
  }

  get currencyRate() {
    return window.Shopify?.currency?.rate ? Number(window.Shopify.currency.rate) : 1;
  }

  cartHasSubscription() {
    return this.getAttribute('data-has-subscription') === 'true';
  }

  decodeText(value) {
    if (!value) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  attr(name, fallback = '') {
    return this.decodeText(this.getAttribute(name) || fallback);
  }

  stringTemplate(key, fallback, replacements) {
    let template = this.decodeText(window.cartStrings?.[key] || fallback);
    Object.keys(replacements).forEach((token) => {
      template = template.split(`{{ ${token} }}`).join(replacements[token]);
    });
    return template;
  }

  refreshFromCart() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        this.syncSubscriptionFromCart(cart);
        this.setAttribute('data-cart-total-amount', String(cart.total_price || 0));
        this.updateState();
      })
      .catch(() => {});
  }

  updatePromoMessage() {
    this.refreshFromCart();
  }

  syncSubscriptionFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const hasSubscription = cart.items.some((item) => !!item.selling_plan_allocation);
    this.setAttribute('data-has-subscription', hasSubscription ? 'true' : 'false');
  }

  shippingDiscountLabel(discount, fallbackLabel) {
    if (fallbackLabel) return fallbackLabel;
    const value = Number(discount);
    if (!Number.isFinite(value) || value <= 0) return 'Shipping discount';
    if (value >= 100) return 'Free shipping';
    return `${value}% off shipping`;
  }

  adjustedThreshold(cents) {
    const value = Number(cents) || 0;
    if (value <= 0) return 0;
    return Math.round(value * this.currencyRate);
  }

  setProgress(cartTotal, threshold) {
    if (!this.barEl || !this.fillEl) return;
    const max = threshold > 0 ? threshold : 1;
    const progressPercent = Math.max(0, Math.min(100, (cartTotal / max) * 100));
    this.barEl.setAttribute('aria-valuenow', String(cartTotal));
    this.barEl.setAttribute('aria-valuemax', String(max));
    this.fillEl.style.width = `${progressPercent}%`;
  }

  renderBadges(badges) {
    if (!this.badgesEl) return;

    this.badgesEl.innerHTML = '';
    if (!badges.length) {
      this.badgesEl.hidden = true;
      return;
    }

    badges.forEach((label, index) => {
      if (index > 0) {
        const plus = document.createElement('span');
        plus.className = 'cart-rewards__plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        this.badgesEl.appendChild(plus);
      }

      const badge = document.createElement('span');
      badge.className = 'cart-rewards__badge';
      badge.textContent = label;
      this.badgesEl.appendChild(badge);
    });

    this.badgesEl.hidden = false;
  }

  placeMark(el, threshold, scale, reached, label) {
    if (!el) return;
    const show = threshold > 0 && scale > 0;
    el.hidden = !show;
    if (!show) return;
    el.style.left = `${(threshold / scale) * 100}%`;
    el.classList.toggle('is-reached', reached);
    if (label) el.setAttribute('aria-label', label);
  }

  updateState() {
    const cartTotal = Number(this.getAttribute('data-cart-total-amount')) || 0;

    if (cartTotal === 0) {
      this.classList.add('is-empty');
      return;
    }
    this.classList.remove('is-empty');

    const currencyRate = this.currencyRate;
    const hasSubscription = this.cartHasSubscription();
    const otpDiscount = Number(this.getAttribute('data-otp-shipping-discount')) || 0;
    const subDiscount = Number(this.getAttribute('data-sub-shipping-discount')) || 0;
    const otpLabel = this.attr('data-otp-shipping-label');
    const subLabel = this.attr('data-sub-shipping-label');
    const subUpsell = this.attr(
      'data-sub-shipping-upsell'
    );
    const otpThreshold = this.adjustedThreshold(this.getAttribute('data-otp-shipping-threshold'));
    const subThreshold = this.adjustedThreshold(this.getAttribute('data-sub-shipping-threshold'));
    const giftThreshold = this.adjustedThreshold(this.getAttribute('data-gift-threshold'));
    const giftLabel = this.attr('data-gift-label');
    const giftBadgeLabel = this.attr('data-gift-success', giftLabel);
    const giftEnabled = giftThreshold > 0;

    const activeDiscount = hasSubscription ? subDiscount : otpDiscount;
    const activeLabel = hasSubscription ? subLabel : otpLabel;
    const activeThreshold = hasSubscription ? subThreshold : otpThreshold;
    const activeShippingLabel = this.shippingDiscountLabel(activeDiscount, activeLabel);
    const shippingUnlocked = activeThreshold <= 0 || cartTotal >= activeThreshold;
    const giftUnlocked = giftEnabled && cartTotal >= giftThreshold;

    // Message / hint above the bar — focus on the next incomplete reward
    let message = '';
    if (!shippingUnlocked) {
      message = this.stringTemplate(
        'shipping_discount_remaining_html',
        `Spend ${this.formatMoney(activeThreshold - cartTotal)} more for ${activeShippingLabel}`,
        {
          remaining_amount: this.formatMoney(activeThreshold - cartTotal),
          discount: activeShippingLabel,
        }
      );
    } else if (giftEnabled && !giftUnlocked) {
      message = this.stringTemplate(
        'free_gift_remaining_html',
        `Spend ${this.formatMoney(giftThreshold - cartTotal)} more for ${giftLabel}`,
        {
          remaining_amount: this.formatMoney(giftThreshold - cartTotal),
          gift_label: giftLabel,
        }
      );
    } else {
      message = '';
    }

    if (this.messageEl) {
      this.messageEl.textContent = message;
      this.messageEl.hidden = !message;
    }

    if (this.hintEl) {
      const showHint = !hasSubscription && !!subUpsell;
      this.hintEl.hidden = !showHint;
      this.hintEl.textContent = showHint ? subUpsell : '';
    }

    const progressScale = Math.max(activeThreshold, giftEnabled ? giftThreshold : 0) || 1;
    this.setProgress(cartTotal, progressScale);
    this.placeMark(this.shippingMark, activeThreshold, progressScale, shippingUnlocked, activeShippingLabel);
    this.placeMark(this.giftMark, giftEnabled ? giftThreshold : 0, progressScale, giftUnlocked, giftLabel);

    // Unlocked rewards below the bar as badges
    const unlockedBadges = [];
    if (shippingUnlocked) unlockedBadges.push(activeShippingLabel);
    if (giftUnlocked) unlockedBadges.push(giftBadgeLabel || giftLabel);
    this.renderBadges(unlockedBadges);

    this.classList.toggle('has-subscription', hasSubscription);
    this.classList.toggle('is-otp', !hasSubscription);
    this.classList.toggle('shipping-unlocked', shippingUnlocked);
    this.classList.toggle('shipping-locked', !shippingUnlocked);
    this.classList.toggle('gift-disabled', !giftEnabled);
    this.classList.toggle('gift-unlocked', giftUnlocked);
    this.classList.toggle('all-unlocked', shippingUnlocked && (!giftEnabled || giftUnlocked));

    this.style.display = 'block';
    this.updateGiftBanner(cartTotal, currencyRate);
  }

  updateGiftBanner(cartTotal, currencyRate) {
    const giftPromoMessage = document.querySelector('[data-promo-message-gift]');
    if (!giftPromoMessage) return;

    const freeGiftAmount = giftPromoMessage.dataset.freeGiftAmount
      ? Number(giftPromoMessage.dataset.freeGiftAmount)
      : 0;

    if (freeGiftAmount > 0) {
      const adjustedGiftThreshold = Math.round(freeGiftAmount * currencyRate);
      giftPromoMessage.style.display = adjustedGiftThreshold - cartTotal <= 0 ? 'none' : 'block';
    }
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = window.Shopify?.format?.money || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    const match = formatString.match(placeholderRegex);
    if (match) {
      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }
    } else {
      value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }
}

if (!customElements.get('shipping-notice')) {
  customElements.define('shipping-notice', ShippingNotice);
}
